import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse } from 'axios'
import {
  Issue,
  IssueCommentPost,
  IssueComments,
  IssueEvents,
  Notifications,
  SingleIssueComment,
  StageLocations,
} from '@/routes/projectStages/issue/types/issue.ts'
import { LoaderFunctionArgs } from 'react-router-dom'
import { STALE_TIME } from '@/lib/constants/constants.ts'
import { RelatedUsers } from '@/routes/projectStages/issue/view/details/DetailsRelatedUsers.tsx'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { currStageIssueTypesQuery } from '@/lib/actions/getStageIssuesTypes.ts'
import { currStageUsersQuery } from '@/lib/actions/getCurrStageUsers.ts'
import { withOfflineMode } from '@/lib/helpers/offlineModeQueryOptions'
import { offlineSafeEnsureQueryData } from '@/lib/helpers/offlineSafeQuery.ts'

// `markNotificationsRead` is opt-in and defaults to false: only a genuine,
// user-initiated open (the route loader) should mark this issue's
// notifications read. All other consumers (the mounted `useCurrentIssueData`
// query, its mount/Mercure-driven background refetches, comments) leave it
// false so a passive refetch doesn't count as "the user saw it" and wrongly
// suppress push/email. Backend gate: IssueStateProvider::MARK_READ_HEADER.
export const singleIssueQuery = (issueId: string, markNotificationsRead = false) =>
  withOfflineMode({
    queryKey: ['singleIssue', { id: issueId }],
    queryFn: () => getSingleIssue(issueId, markNotificationsRead),
    // Always consider data stale so we refetch on mount
    staleTime: 0,
    // Always refetch when component mounts to get fresh data
    refetchOnMount: 'always' as const,
  })

export const singleNotificationQuery = (eventId: number) => ({
  queryKey: ['eventNotification', { id: eventId }],
  queryFn: () => getSingleNotification(eventId),
  staleTime: STALE_TIME,
})

export const singleIssueCommentsQuery = ({
  issueId,
  pageParam,
}: {
  issueId: string
  pageParam: number
}) =>
  withOfflineMode({
    queryKey: ['singleIssueComments', { id: issueId }],
    queryFn: () => getSingleIssueComments({ pageParam, issueId }),
    initialPageParam: 0,
    staleTime: 1000 * 30,
    refetchOnMount: true as const,
  })

export const singleIssueEventsQuery = ({
  issueId,
  pageParam,
}: {
  issueId: string
  pageParam: number
}) =>
  withOfflineMode({
    queryKey: ['singleIssueEvents', { id: issueId }],
    queryFn: () => getSingleIssueEvents({ pageParam, issueId }),
    initialPageParam: 0,
    staleTime: 1000 * 30,
    refetchOnMount: true as const,
  })

export const stageLocationsQuery = (projectStageId: string | undefined) => ({
  queryKey: ['stageLocations', { id: projectStageId }],
  queryFn: () => getProjectLocations(projectStageId),
  staleTime: 1000 * 60 * 5,
})

export const loaderSingleIssue = async ({ params }: LoaderFunctionArgs) => {
  if (!params.issueId || !params.projectStageId) {
    throw new Error('No issue ID provided')
  }

  const safe = <T>(q: { queryKey: unknown[]; queryFn: () => Promise<T> }) =>
    offlineSafeEnsureQueryData(queryClient, q.queryKey, q.queryFn).catch(() => undefined)

  // Genuine user open → mark this issue's notifications read (interactive
  // header). This MUST run even when the issue is already cached: the offline
  // prefetch seeds every `singleIssue` on native, so the cache-first render
  // path below (`safe(...)`) would return instantly and never invoke the
  // header-bearing queryFn — leaving notifications unread on every native open
  // and defeating the fix. So fire the header GET DIRECTLY (bypassing the
  // cache-first helper) and write the fresh response back to the query cache.
  // Keep it OFF the awaited render path (fire-and-forget) so a cached open
  // still renders instantly and navigation isn't blocked on the network; the
  // mark-read is a side effect the page doesn't need to wait for. Only the
  // route loader does this — background/Mercure refetches never carry the
  // header (see getSingleIssue / IssueStateProvider::MARK_READ_HEADER).
  const issueId = params.issueId
  void getSingleIssue(issueId, true)
    .then(issue => {
      if (issue) queryClient.setQueryData(singleIssueQuery(issueId).queryKey, issue)
    })
    .catch(() => undefined)

  try {
    await Promise.all([
      // Render path: cache-first + offline-safe. No header — the mark-read is
      // handled once, above, so this stays a pure read.
      safe(singleIssueQuery(params.issueId)),
      safe(currStageIssueTypesQuery(params.projectStageId)),
      safe(stageLocationsQuery(params.projectStageId)),
      safe(currStageUsersQuery(params.projectStageId)),
    ])

    return {
      issueId: params.issueId,
      projectStageId: params.projectStageId,
      issueTypeId: params.issueTypeId,
    }
  } catch (error) {
    // Check if this is a network-related error
    const isNetworkError =
      !navigator.onLine ||
      (error instanceof Error &&
        (error.message.toLowerCase().includes('network') ||
          error.message.toLowerCase().includes('failed to fetch') ||
          (error as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      console.log('[loaderSingleIssue] Network error, returning params to allow offline mode')
      return {
        issueId: params.issueId,
        projectStageId: params.projectStageId,
        issueTypeId: params.issueTypeId,
      }
    }

    return handleApiErr(error)
  }
}
export const getSingleIssue = async (issueId: string, markNotificationsRead = false) => {
  if (!issueId) return null
  // Only a genuine user-open (route loader) sends this header; the backend
  // marks this issue's notifications read solely when it's present, so
  // background/Mercure-driven refetches don't clear them. See
  // IssueStateProvider::MARK_READ_HEADER.
  const response = await api.get(
    `/issues/${issueId}`,
    markNotificationsRead ? { headers: { 'X-Mark-Notifications-Read': '1' } } : undefined,
  )
  return response.data as Issue
}

export const editIssueAttachments = async (
  issue: Pick<Issue, 'id'> & {
    attachments: (string | undefined)[]
  },
): Promise<AxiosResponse<Issue>> => {
  return api.patch(`/issues/${issue.id}`, issue)
}

export const editIssue = async (issue: Partial<Issue>): Promise<AxiosResponse<Issue>> => {
  return api.patch(`/issues/${issue.id}`, issue)
}

export const postRelatedUsers = async (
  issueId: number,
  relatedUsers: RelatedUsers,
): Promise<AxiosResponse<Issue>> => {
  return api.post(`/issues/${issueId}/subscriptions`, relatedUsers)
}

export const deleteRelatedUser = async (issueId: number): Promise<AxiosResponse<Issue>> => {
  return api.delete(`/issues/${issueId}/subscriptions`)
}
export const getSingleIssueComments = async ({
  pageParam,
  issueId,
}: {
  issueId: string | null | undefined
  pageParam: number
}) => {
  if (!issueId) return null

  const response = await api.get(
    `/issues/${issueId}/comments?page=${pageParam === 0 ? 1 : pageParam}`,
  )
  return response.data as IssueComments
}

export const getSingleIssueEvents = async ({
  pageParam,
  issueId,
}: {
  issueId: string | null
  pageParam: number
}) => {
  if (!issueId) return null

  const response = await api.get(
    `/issues/${issueId}/issue_events?page=${pageParam === 0 ? 1 : pageParam}`,
  )
  return response.data as IssueEvents
}

export const getProjectLocations = async (projectStageId: string | undefined) => {
  if (!projectStageId) return null

  const response = await api.get(`/project_stages/${projectStageId}/locations`)
  return response.data['hydra:member'] as StageLocations
}

export const getSingleNotification = async (eventId: number) => {
  if (!eventId) return null
  const response = await api.get(`/issue_events/${eventId}/notifications`)
  return response.data['hydra:member'] as Notifications
}

export const newIssueComment = async (
  issueComment: IssueCommentPost,
): Promise<AxiosResponse<SingleIssueComment>> => {
  return api.post(`/comments`, issueComment)
}

export const deleteIssueComment = async (commentId: number): Promise<AxiosResponse<Issue>> => {
  return api.delete(`/comments/${commentId}`)
}

export const patchIssueComment = async (
  issueComment: Partial<SingleIssueComment>,
): Promise<AxiosResponse<SingleIssueComment>> => {
  return api.patch(`/comments/${issueComment.id}`, issueComment)
}
